import {
  collection, doc, addDoc, deleteDoc, updateDoc, onSnapshot, query, where,
  runTransaction, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebaseDDH';
import { normalizeEmail } from './clients';
import { BUSINESS } from '../constants/business';

const COLLECTION = 'invoices';

// Pending serverTimestamps read back as null; 'estimate' substitutes the local
// clock so a just-raised invoice shows its date immediately instead of blank.
const ESTIMATE = { serverTimestamps: 'estimate' };
const COUNTER = doc(db, 'counters', 'invoices');

export const PAYMENT_DAYS = 14;

// Invoice numbers must be unique and unbroken — HMRC expects a sequential
// series, so Firestore's auto-IDs won't do. A transaction reads the counter
// and writes it back atomically, so two invoices raised at the same moment
// can't take the same number or skip one.
async function nextInvoiceNumber() {
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(COUNTER);
    const next = snap.exists() ? Number(snap.data().next) || 1 : 1;
    tx.set(COUNTER, { next: next + 1 }, { merge: true });
    return next;
  });
}

export function formatInvoiceNumber(n) {
  return `INV-${String(n).padStart(4, '0')}`;
}

export function isOverdue(invoice) {
  if (!invoice?.dueAt || invoice.status === 'paid') return false;
  return invoice.dueAt.toDate() < new Date();
}

export const KIND_LABELS = {
  stage: 'Project',
  care: 'Care plan',
};

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// JS rolls overflowing dates forward — 31 Jan plus a month lands on 3 March.
// setDate(0) walks back to the last day of the intended month, so a plan
// starting on the 31st bills on the 28th/30th in shorter months.
function addMonths(date, n) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + n);
  if (d.getDate() !== day) d.setDate(0);
  return d;
}

export function formatDay(date) {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : date;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Care plans bill by anniversary rather than calendar month: the next period
// starts the day after the last one ended, so a plan starting mid-month never
// charges a client twice within a few days.
export function nextCarePeriod(careInvoices) {
  const last = careInvoices.find((invoice) => invoice.periodEnd);
  const start = last ? addDays(last.periodEnd.toDate(), 1) : new Date();
  const end = addDays(addMonths(start, 1), -1);
  return { start, end };
}

export function carePeriodDue(period) {
  return new Date() >= period.start;
}

// `stageIndex` ties a stage invoice back to the payment stage it bills, so the
// admin UI can tell which stages have already been raised.
export async function createInvoice({
  clientEmail, clientName, quoteId, quoteTitle, kind, stageIndex, description, amount, period,
}, createdByEmail) {
  const number = await nextInvoiceNumber();
  return addDoc(collection(db, COLLECTION), {
    number,
    reference: formatInvoiceNumber(number),
    clientEmail: normalizeEmail(clientEmail),
    clientName: clientName || null,
    quoteId: quoteId || null,
    quoteTitle: quoteTitle || null,
    kind,
    stageIndex: stageIndex ?? null,
    // Care plans only: the span this invoice covers. Stored so the next
    // period can start exactly where this one ended.
    periodStart: period ? Timestamp.fromDate(period.start) : null,
    periodEnd: period ? Timestamp.fromDate(period.end) : null,
    description,
    amount: Number(amount) || 0,
    issuedAt: serverTimestamp(),
    dueAt: Timestamp.fromDate(new Date(Date.now() + PAYMENT_DAYS * 24 * 60 * 60 * 1000)),
    status: 'unpaid',
    paidAt: null,
    createdBy: createdByEmail,
    // Snapshotted so an old invoice keeps the details it was issued with —
    // changing your address or bank shouldn't rewrite what a client was told
    // to pay into last year.
    business: {
      name: BUSINESS.name,
      addressLines: BUSINESS.addressLines,
      companyNumber: BUSINESS.companyNumber,
      bank: BUSINESS.bank,
    },
  });
}

export function watchInvoicesForClient(clientEmail, onData, onError) {
  const q = query(collection(db, COLLECTION), where('clientEmail', '==', normalizeEmail(clientEmail)));
  return onSnapshot(
    q,
    (snapshot) => {
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) }));
      docs.sort((a, b) => (b.number || 0) - (a.number || 0));
      onData(docs);
    },
    onError,
  );
}

export function watchAllInvoices(onData, onError) {
  return onSnapshot(
    collection(db, COLLECTION),
    (snapshot) => {
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) }));
      docs.sort((a, b) => (b.number || 0) - (a.number || 0));
      onData(docs);
    },
    onError,
  );
}

export async function markPaid(id, paid) {
  await updateDoc(doc(db, COLLECTION, id), {
    status: paid ? 'paid' : 'unpaid',
    paidAt: paid ? serverTimestamp() : null,
  });
}

// Only ever for an invoice raised in error — a paid one is blocked by the
// rules, and a real correction is a credit note rather than a deletion.
export async function deleteInvoice(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}
